import React from 'react'
import { Avatar, Slider, SliderThumb } from '@mui/material'
import { alpha, styled } from '@mui/system'
import { Color } from '../../../../../../../../../styles/commonStyles'
import { colorPalette } from '../../../../../../../../style/colorPalette'
import { WbsItemTypeVO } from '../../../../../../../../../domain/value-object/WbsItemTypeVO'
import { isGanttSummaryType } from '../ganttUtil'

// ---------- Color constants ----------

export const SliderColorLegacy = {
  TASK: '#888888',
  NOT_SCHEDULED: '#cccccc',
  ANCESTORS: '#686868',
} as const

export const SliderColorsV2 = {
  TASK: colorPalette.blue[4],
  NOT_SCHEDULED: colorPalette.monotone[3],
  ANCESTORS: colorPalette.blue[4],
  PROGRESS_FILL: colorPalette.blue[7],
} as const

// MUI Slider root height:4px, padding:0 → track中心 = root top + 2px
export const SLIDER_TRACK_CENTER = 2

// ---------- Types ----------

export type StyledSliderProps = {
  // Reduce console errors
  scheduled: number
  wbsitemtype?: WbsItemTypeVO
  disablethumbs: boolean[]
  index: number
  isv2?: number
  overrunleft?: number
  overrunwidth?: number
}

type StyledSliderThumbProps = {
  disabled?: boolean
  labeldisabled?: number
  thumb?: number
}

// ---------- Helpers ----------

export function getSliderColor(props: StyledSliderProps) {
  const colors = props.isv2 ? SliderColorsV2 : SliderColorLegacy
  if (!props.scheduled) {
    return colors.NOT_SCHEDULED
  } else if (!props.wbsitemtype?.isTask()) {
    return colors.ANCESTORS
  }
  return colors.TASK
}

// ---------- Layout ----------

export const SliderWrapper = styled('div')({
  position: 'relative',
  width: '100%',
  height: '100%',
})

export const ProgressGauge = styled('div')<{ $barHeight: number }>(
  ({ $barHeight }) => ({
    position: 'absolute',
    height: `${$barHeight}px`,
    top: `${SLIDER_TRACK_CENTER}px`,
    transform: 'translateY(-50%)',
    backgroundColor: SliderColorsV2.PROGRESS_FILL,
    borderRadius: '2px',
    zIndex: 1,
    pointerEvents: 'none' as const,
  })
)

// ---------- Label base ----------

const labelBase = {
  position: 'absolute' as const,
  top: `${SLIDER_TRACK_CENTER}px`,
  transform: 'translateY(-50%)',
  display: 'flex',
  flexDirection: 'row' as const,
  alignItems: 'center',
  gap: '3px',
  pointerEvents: 'all' as const,
  whiteSpace: 'nowrap' as const,
  zIndex: 2,
  cursor: 'default',
}

export const InsideLabel = styled('div')<{
  $left: number
  $width: number
  $isParent?: boolean
}>(({ $left, $width, $isParent }) => ({
  ...labelBase,
  left: `${$left}%`,
  width: `${$width}%`,
  height: $isParent ? '32px' : '20px',
  padding: '0 4px',
  overflow: 'hidden',
}))

export const RightLabel = styled('div')<{
  $left: number
  $isParent?: boolean
  $maxWidth: string
}>(({ $left, $isParent, $maxWidth }) => ({
  ...labelBase,
  left: `${$left}%`,
  height: $isParent ? '24px' : '20px',
  padding: '0 6px',
  marginLeft: '4px',
  backgroundColor: colorPalette.blue[0] + 'E6',
  borderRadius: '4px',
  maxWidth: $maxWidth,
  overflow: 'hidden',
}))

export const LeftLabel = styled('div')<{
  $barLeft: number
  $isParent?: boolean
  $maxWidth: string
}>(({ $barLeft, $isParent, $maxWidth }) => ({
  ...labelBase,
  right: `${100 - $barLeft}%`,
  height: $isParent ? '24px' : '20px',
  padding: '0 6px',
  marginRight: '4px',
  backgroundColor: colorPalette.blue[0] + 'E6',
  borderRadius: '4px',
  maxWidth: $maxWidth,
  overflow: 'hidden',
}))

export const BarHoverArea = styled('div')<{ $left: number; $width: number }>(
  ({ $left, $width }) => ({
    position: 'absolute',
    top: `${SLIDER_TRACK_CENTER}px`,
    transform: 'translateY(-50%)',
    left: `${$left}%`,
    width: `${$width}%`,
    height: '20px',
    cursor: 'default',
    zIndex: 2,
  })
)

// ---------- Label content ----------

export const StatusIcon = styled('div')<{
  $color: string
  $isParent?: boolean
}>(({ $color, $isParent }) => ({
  width: $isParent ? '14px' : '10px',
  height: $isParent ? '14px' : '10px',
  minWidth: $isParent ? '14px' : '10px',
  borderRadius: '50%',
  backgroundColor: $color,
  border: `1px solid ${alpha(colorPalette.monotone[10], 0.13)}`,
  pointerEvents: 'none',
  flexShrink: 0,
}))

export const AssigneeAvatar = styled(Avatar)<{ $isParent?: boolean }>(
  ({ $isParent }) => ({
    width: $isParent ? '20px' : '16px',
    height: $isParent ? '20px' : '16px',
    minWidth: $isParent ? '20px' : '16px',
    fontSize: $isParent ? '12px' : '10px',
    marginLeft: '1px',
    color: colorPalette.monotone[10],
    pointerEvents: 'all',
    cursor: 'default',
    flexShrink: 0,
    fontWeight: 'normal',
  })
)

export const NameLabel = styled('span')<{
  $inside?: boolean
  $isParent?: boolean
}>(({ $inside, $isParent }) => ({
  fontSize: $isParent ? '12px' : '10px',
  fontWeight: 'bold',
  lineHeight: $isParent ? '20px' : '16px',
  color: $inside ? colorPalette.monotone[0] : colorPalette.monotone[6],
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
}))

export const ProgressRateLabel = styled('span')<{
  $inside?: boolean
  $isParent?: boolean
  $showSeparator?: boolean
}>(({ $inside, $isParent, $showSeparator = true }) => ({
  fontSize: $isParent ? '12px' : '10px',
  fontWeight: 'bold',
  marginLeft: $showSeparator ? '2px' : 0,
  paddingLeft: $showSeparator ? '5px' : 0,
  borderLeft: $showSeparator
    ? $inside
      ? `1px solid ${alpha(colorPalette.monotone[0], 0.4)}`
      : `1px solid ${colorPalette.monotone[3]}`
    : 'none',
  color: $inside ? colorPalette.monotone[0] : colorPalette.monotone[6],
  pointerEvents: 'none',
  flexShrink: 0,
}))

// ---------- Slider ----------

export const StyledSlider = styled(Slider)<StyledSliderProps>(props => {
  const sliderColor = getSliderColor(props)

  if (props.isv2) {
    const isParent = isGanttSummaryType(props.wbsitemtype)
    const barHeight = isParent ? 32 : 20
    return {
      height: '4px',
      padding: 0, // MUI size=small の padding:13px を除去し、track位置を予測可能にする
      overflow: 'visible', // barHeight がroot(4px)を超えても描画されるようにする
      width: '100%',
      display: 'inline-block',
      verticalAlign: 'top', // baseline だと親コンテナ内で下にずれるため
      position: 'relative',
      touchAction: 'none',
      pointerEvents: 'none',
      margin: 0,
      '& .MuiSlider-root': {
        color: sliderColor,
      },
      '& .MuiSlider-rail': {
        height: 0,
      },
      '& .MuiSlider-track': {
        display: 'block',
        position: 'absolute',
        pointerEvents: props.disablethumbs.some(v => !!v) ? 'none' : 'all',
        cursor: 'pointer',
        height: `${barHeight}px`,
        backgroundColor: 'transparent',
      },
      '& .MuiSlider-track::after': {
        content: '""',
        position: 'absolute',
        opacity: 0.85,
        transition: 'all 0.1s ease !important',
        width: '100%',
        height: `${barHeight}px`,
        backgroundColor: !props.scheduled ? 'transparent' : sliderColor,
        backgroundImage: !props.scheduled
          ? `repeating-linear-gradient(-90deg, transparent, transparent 2px, ${sliderColor} 2px, ${sliderColor} 5px)`
          : undefined,
        top: '50%',
        transform: 'translateY(-50%)',
        borderRadius: '2px',
      },
      '& .MuiSlider-valueLabel:before': {
        bottom: props.index === 0 ? undefined : 0,
        top: props.index === 0 ? '-8px' : undefined,
      },
      '&::after': props.overrunwidth
        ? {
            content: '""',
            position: 'absolute',
            left: `${props.overrunleft}%`,
            width: `${props.overrunwidth}%`,
            height: `${barHeight}px`,
            top: '50%',
            transform: 'translateY(-50%)',
            background: `repeating-linear-gradient(45deg, ${colorPalette.yellow[1]}40, ${colorPalette.yellow[1]}40 4px, ${colorPalette.yellow[5]}60 4px, ${colorPalette.yellow[5]}60 5px)`,
            boxSizing: 'border-box',
            borderRadius: '0 2px 2px 0',
            pointerEvents: 'none',
            zIndex: 0,
          }
        : {},
    }
  }

  // Legacy styles
  const isWorkGroupOrProcess =
    props.wbsitemtype?.isWorkgroup() || props.wbsitemtype?.isProcess()
  const isArrow =
    props.wbsitemtype?.isDeliverableList() || props.wbsitemtype?.isDeliverable()
  return {
    height: '4px',
    width: '100%',
    display: 'inline-block',
    position: 'relative',
    touchAction: 'none',
    pointerEvents: 'none',
    margin: 0,
    zIndex: 3,
    '& .MuiSlider-root': {
      color: sliderColor,
    },
    '& .MuiSlider-rail': {
      height: 0,
    },
    '& .MuiSlider-track': {
      display: 'block',
      position: 'absolute',
      pointerEvents: props.disablethumbs.some(v => !!v) ? 'none' : 'all',
      cursor: 'pointer',
      height: `10px`,
      backgroundColor: 'transparent',
    },
    '& .MuiSlider-track::after': {
      content: '""',
      position: 'absolute',
      opacity: props.wbsitemtype?.isTask() ? 0.75 : 1,
      transition: 'all 0.1s ease !important',
      width: '100%',
      height: `${isWorkGroupOrProcess || isArrow ? 10 : 3}px`,
      backgroundColor: !props.scheduled ? 'transparent' : sliderColor,
      backgroundImage: !props.scheduled
        ? `repeating-linear-gradient(-90deg, transparent, transparent 2px, ${sliderColor} 2px, ${sliderColor} 5px)`
        : undefined,
      transform: `translateY(${isWorkGroupOrProcess || isArrow ? 1 : 3.5}px)`,
      borderRadius: isWorkGroupOrProcess ? 1 : 'inherit',
      clipPath:
        isWorkGroupOrProcess && !props.disablethumbs[1]
          ? 'polygon(calc(100% - 4px) 100%, 100% calc(50% + 0.5px), 100% calc(50% - 0.5px), calc(100% - 4px) 0, 0 0, 0 100%)'
          : isArrow && !props.disablethumbs[1]
            ? 'polygon(0 3.5px, calc(100% - 5px) 3.5px, calc(100% - 5px) 0, 100% 50%, calc(100% - 5px) 100%, calc(100% - 5px) calc(100% - 3.5px), 0 calc(100% - 3.5px))'
            : isArrow && props.disablethumbs[1]
              ? 'polygon(0 3.5px, 100% 3.5px, 100% calc(100% - 3.5px), 0 calc(100% - 3.5px))'
              : undefined,
    },
    '& .MuiSlider-valueLabel:before': {
      bottom: props.index === 0 ? undefined : 0,
      top: props.index === 0 ? '-8px' : undefined,
    },
  }
})

// ---------- Slider Thumb ----------

const StyledSliderThumbBase = styled(SliderThumb)<StyledSliderThumbProps>(
  props => {
    const ownerState = props['ownerState'] as StyledSliderProps
    const { wbsitemtype, index, isv2 } = ownerState
    const transparentThumb =
      isv2 ||
      wbsitemtype?.isWorkgroup() ||
      wbsitemtype?.isProcess() ||
      ((wbsitemtype?.isDeliverableList() || wbsitemtype?.isDeliverable()) &&
        props.thumb === 1)
    const topValue = 'calc(50% + 1px)'
    const hoverStyle = {
      boxShadow: `0 0 0 0.25rem ${alpha(Color.GREY, 0.15)}`,
      transform: `scale(1.5)`,
    }
    const invisibleDefaultHoverStyle = {
      boxShadow: `0 0 0 0 transparent`,
    }
    return {
      backgroundColor: 'transparent',
      top: topValue,
      height: `${props.disabled ? 0 : 20}px`,
      width: `${props.disabled ? 0 : 20}px`,
      borderRadius: '50%',
      color: transparentThumb ? 'transparent' : getSliderColor(ownerState),
      boxSizing: 'border-box',
      outline: 0,
      pointerEvents: 'all',
      cursor: 'pointer',
      margin: 0,
      '&::after': {
        height: `${props.disabled ? 0 : transparentThumb ? 10 : 8}px`,
        width: `${props.disabled ? 0 : transparentThumb ? 10 : 8}px`,
        borderRadius: '50%',
        transition: 'all 0.1s ease !important',
        transformOrigin: `${transparentThumb ? 15 : 11}px ${
          transparentThumb ? 15 : 11
        }px`,
        backgroundColor: transparentThumb
          ? 'transparent'
          : getSliderColor(ownerState),
      },
      ':hover': invisibleDefaultHoverStyle,
      '&.Mui-focusVisible': invisibleDefaultHoverStyle,
      '&.Mui-active': invisibleDefaultHoverStyle,
      ':hover::after': hoverStyle,
      '&.Mui-focusVisible::after': hoverStyle,
      '&.Mui-active::after': hoverStyle,
      '& .MuiSlider-valueLabel.MuiSlider-valueLabelOpen': {
        transform: `translateY(${index === 0 ? 40 : -120}%) scale(0.9)`,
      },
      '& .MuiSlider-valueLabel': {
        top: `${index === 0 ? 54 : 0}%`,
        transition: 'all 0.1s ease !important',
        visibility:
          props.disabled || props.labeldisabled ? 'hidden' : undefined,
        zIndex: 10,
      },
    }
  }
)

type SliderThumbComponentProps = React.PropsWithChildren<
  React.ComponentPropsWithoutRef<typeof SliderThumb>
> & {
  'data-index'?: number
  ownerState: StyledSliderProps & { value: number[]; min: number; max: number }
}

export function StyledSliderThumb(props: SliderThumbComponentProps) {
  const { children, ...other } = props
  const thumb = other['data-index']
  const { disablethumbs, value, min, max } = props.ownerState
  return (
    <StyledSliderThumbBase
      {...other}
      thumb={thumb}
      disabled={
        (thumb === 0 && disablethumbs[0]) || (thumb === 1 && disablethumbs[1])
      }
      labeldisabled={
        +(
          (thumb === 0 && value[thumb] === min) ||
          (thumb === 1 && value[thumb] === max)
        )
      }
    >
      {children}
    </StyledSliderThumbBase>
  )
}
